import { Slider } from 'antd';
import AlertIcons from 'components/AlertIcons';
import { usePerms } from 'contexts/PermsContext';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePostAlertMutation } from 'services/alerts/alerts.api';
import { getJurisdictions } from 'services/geography/geography.service';
import { alertColorScale, getSeverityLevel } from 'utils/alertUtils';
import { validateJurisdictionBoundaries } from 'utils/geometryUtils';
import LocationSearch from './components/LocationSearch';
import MoveablePoints from './components/MoveablePoints';

////////////
// Render //
////////////
export default function AlertCreator({
  onSubmitCallback = null,
  timeInputMode = 'setTime',
  data = null,
  setData = null,
  widget,
  dimensions,
}) {
  // Leaflet Specific States
  const [mapCenter, setMapCenter] = useState([Cookies.get('latitude'), Cookies.get('longitude')]);
  const [postAlert, { isSuccess }] = usePostAlertMutation();

  const [bounds, setMapBounds] = useState([
    [Cookies.get('latitude'), Cookies.get('longitude')],
    [Cookies.get('latitude'), Cookies.get('longitude')],
  ]);
  const [zoom, setZoom] = useState(13);
  const [getMapCenter, setGetMapCenter] = useState([0, 0]);
  const [jurisdictions, setJurisdictions] = useState(null);

  const perms = usePerms();
  const navigate = useNavigate();

  // Data states
  const [alertData, setAlertData] = useState({
    coordinates: [],
    city: '',
    state: '',
    issuedTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
    expirationTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
    severity: 25,
    headline: '',
    description: '',
    type: '',
    isInternalUse: false,
    searchLocation: '',
    foundLocation: '',
    duration: 0,
    mapCenter: '',
  });

  useEffect(() => {
    (async () => {
      if (!(await perms.validatePermissions(['Create-EditAlerts']))) {
        navigate('/404');
        return;
      }
    })();

    if (
      data &&
      (data?.coordinates === null ||
        data?.city === null ||
        data?.state === null ||
        data?.issuedTime === null ||
        data?.expirationTime === null ||
        data?.severity === null ||
        data?.headline === null ||
        data?.description === null ||
        data?.type === null ||
        data?.isInternalUse === null ||
        data?.searchLocation === null ||
        data?.foundLocation === null ||
        data?.duration === null ||
        data?.mapCenter === null)
    ) {
      setData({
        coordinates: [],
        city: '',
        state: '',
        issuedTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
        expirationTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
        severity: 25,
        headline: '',
        description: '',
        type: '',
        isInternalUse: false,
        searchLocation: '',
        foundLocation: '',
        duration: 0,
        mapCenter: '',
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      const res = await getJurisdictions();
      setJurisdictions(res.data.result.filter((f) => f !== null));
    })();
  }, []);

  // Rendered States
  const [validationErr, setValidationErr] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [pendingMsg, setPendingMsg] = useState('');

  // Check screen width
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Get user location to zoom in to them on map
  useEffect(() => {
    data?.mapCenter
      ? setMapCenter(data.mapCenter)
      : setMapCenter([Cookies.get('latitude'), Cookies.get('longitude')]);

    setZoom(14);
  }, []);

  const updateData = (newValue) => {
    let error = false;
    if (data ? !data.type : !alertData.type) {
      setValidationErr('Alert type cannot be blank');
      error = true;
    }
    if (data ? !data.headline : !alertData.headline) {
      setValidationErr('Headline cannot be blank');
      error = true;
    }
    if (data ? !data.description : !alertData.description) {
      setValidationErr('Description cannot be blank');
      error = true;
    }
    if (data ? !data.state : !alertData.state) {
      setValidationErr('State cannot be blank');
      error = true;
    }

    if (!error) {
      setData({ ...data, ...newValue, error: false });
      setValidationErr('');
    } else {
      setData({ ...data, error: true });
    }
  };

  useLayoutEffect(() => {
    function updateSize() {
      setIsMobile(window.innerWidth < 1555);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const removeLastCoordinate = () => {
    // find index matching last inserted
    let coord = data
      ? data.lastInserted[data.lastInserted.length - 1]
      : alertData.lastInserted[alertData.lastInserted.length - 1];

    const newData = {
      coordinates: [],
      lastInserted: [],
    };

    const truncData = (c, index) => {
      if (c.lat !== coord.lat || c.lng !== coord.lng) newData.coordinates.push(c);
    };

    const truncLastInserted = (i, index) => {
      if (index < (data ? data.lastInserted.length : alertData.lastInserted.length) - 1)
        newData.lastInserted.push(i);
    };

    data ? data.coordinates.forEach(truncData) : alertData.coordinates.forEach(truncData);
    data
      ? data.lastInserted.forEach(truncLastInserted)
      : alertData.lastInserted.forEach(truncLastInserted);

    setData
      ? updateData({
          ...data,
          coordinates: newData.coordinates,
          lastInserted: newData.lastInserted,
        })
      : setAlertData((prevData) => ({
          ...prevData,
          coordinates: newData.coordinates,
          lastInserted: newData.lastInserted,
        }));
  };

  const removeAllCoordinates = () => {
    setData
      ? updateData({
          ...data,
          coordinates: [],
          lastInserted: [],
        })
      : setAlertData((prevData) => ({
          ...prevData,
          coordinates: [],
          lastInserted: [],
        }));
  };

  // Handles input fields
  const handleChange = (e) => {
    const { name, value } = e.target;

    setData
      ? updateData({
          ...data,
          [name]: value,
        })
      : setAlertData((prevData) => ({
          ...prevData,
          [name]: value,
        }));
  };

  // Changes funky time string stuff to UTC for db
  const handleTimeString = (e) => {
    const { name, value } = e.target;

    const date = new Date(value);
    const newValue = date.toISOString();

    setData
      ? updateData({
          ...data,
          [name]: newValue,
        })
      : setAlertData((prevData) => ({
          ...prevData,
          [name]: newValue,
        }));
  };

  // Form validator + API call
  const handleSubmit = async () => {
    setPendingMsg('Creating Alert');

    if (data ? data.coordinates.length < 3 : alertData.coordinates.length < 3) {
      setValidationErr('Coordinates must have 3 or more points');
      setPendingMsg('');
      return;
    }

    // Check if all coordinates are within jurisdiction boundaries
    if (
      !validateJurisdictionBoundaries(
        data ? data.coordinates : alertData.coordinates,
        jurisdictions,
      )
    ) {
      setValidationErr(
        'At least one coordinate within the current alert is outside of your alerting jurisdiction displayed on the map.',
      );
      setPendingMsg('');
      return;
    }

    if (data ? !data.type : !alertData.type) {
      setValidationErr('Alert type cannot be blank');
      setPendingMsg('');
      return;
    }
    if (data ? !data.headline : !alertData.headline) {
      setValidationErr('Headline cannot be blank');
      setPendingMsg('');
      return;
    }
    if (data ? !data.description : !alertData.description) {
      setValidationErr('Description cannot be blank');
      setPendingMsg('');
      return;
    }
    if (data ? !data.state : !alertData.state) {
      setValidationErr('State cannot be blank');
      setPendingMsg('');
      return;
    }

    if (
      data
        ? data.expirationTime <= data.issuedTime
        : alertData.expirationTime <= alertData.issuedTime
    ) {
      setValidationErr('Expiration time cannot be before or at start time');
      setPendingMsg('');
      return;
    }

    setValidationErr('');

    if (!onSubmitCallback) {
      // Further processing or API calls can be done here
      try {
        await postAlert(alertData).unwrap();
        setSuccessMsg('Alert successfully sent!');
        setPendingMsg('');
        setAlertData({
          coordinates: [],
          city: '',
          state: '',
          issuedTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
          expirationTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
          severity: 25,
          headline: '',
          description: '',
          type: '',
          isInternalUse: false,
          searchLocation: '',
          foundLocation: '',
          duration: 0,
          mapCenter: '',
        });
        setTimeout(() => setSuccessMsg(''), 5000);
      } catch (error) {
        if (error.response.status === 403) {
          setValidationErr(error.response.data.message);
          setPendingMsg('');
        } else {
          setValidationErr('Error sending alert');
          setPendingMsg('');
        }
      }
    }
  };

  const reset = () => {
    setAlertData({
      coordinates: [],
      city: '',
      state: '',
      issuedTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
      expirationTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
      severity: 25,
      headline: '',
      description: '',
      type: '',
      isInternalUse: false,
      searchLocation: '',
      foundLocation: '',
      duration: 0,
      mapCenter: '',
    });

    setSuccessMsg('');
    setValidationErr('');
    setPendingMsg('');
  };

  if (!perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditAlerts'])) return null;

  if (!widget)
    return (
      <Container fluid className="d-flex flex-column h-100 py-3">
        <div className="flex-shrink-0">
          <h3>Alert Creator</h3>
          {validationErr && <p className="text-danger">{validationErr}</p>}
          {successMsg && <p className="text-success">{successMsg}</p>}
        </div>
        <div className="d-flex flex-column flex-lg-row gap-3 align-items-stretch flex-grow-1 overflow-auto min-h-0">
          <div id="map-container" className="bg-primary flex-grow-full ratio-1 min-h-0">
            <MoveablePoints
              mapCenter={mapCenter}
              zoom={zoom}
              alertData={!data ? alertData : data.coordinates ? data : { coordinates: [] }}
              setAlertData={setData ? updateData : setAlertData}
              isMobile={isMobile}
              bounds={bounds}
              setGetMapCenter={setGetMapCenter}
              jurisdictions={jurisdictions}
            />
          </div>

          <div className="flex-grow-full d-flex flex-column gap-2 min-h-0">
            <div className="bg-primary p-2 rounded">
              <LocationSearch
                alertData={data ? data : alertData}
                setMapCenter={setMapCenter}
                mapCenter={mapCenter}
                handleChange={handleChange}
                searchLocation={data ? data.searchLocation : alertData.searchLocation}
                getMapCenter={getMapCenter}
                setAlertData={setData ? updateData : setAlertData}
              />
            </div>

            <div className="d-flex flex-column flex-sm-row gap-2 w-100">
              <div className="d-flex flex-grow-full align-items-center gap-1 bg-primary p-2 rounded">
                <div className="d-none d-sm-block">
                  <AlertIcons type={data ? data.type : alertData.type} />
                </div>
                <select
                  className="form-control bg-primary"
                  name="type"
                  value={data ? data.type : alertData.type}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Alert Type *
                  </option>
                  {eventTypes.map((event) => (
                    <option key={event} value={event}>
                      {event}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-grow-full bg-primary p-2 rounded">
                <div>
                  <p
                    className="rounded p-1"
                    style={{
                      backgroundColor: alertColorScale(data ? data.severity : alertData.severity),
                    }}
                  >
                    Severity: {getSeverityLevel(data ? data.severity : alertData.severity)}
                  </p>
                </div>
                <Slider
                  name="severity"
                  min={0}
                  max={100}
                  step={1}
                  value={0 + (data ? data.severity : alertData.severity)}
                  onChange={(event) => {
                    setData
                      ? updateData(
                          data
                            ? { ...data, severity: 0 + event }
                            : { ...alertData, severity: 0 + event },
                        )
                      : setAlertData(
                          data
                            ? { ...data, severity: 0 + event }
                            : { ...alertData, severity: 0 + event },
                        );
                  }}
                />
              </div>
            </div>
            <input
              className="form-control bg-primary"
              name="headline"
              value={data ? data.headline : alertData.headline}
              onChange={handleChange}
              placeholder="Headline *"
            />

            <textarea
              className="form-control bg-primary"
              name="description"
              value={data ? data.description : alertData.description}
              onChange={handleChange}
              placeholder="Description *"
            />

            <div className="d-flex flex-row gap-1">
              <input
                className="form-control bg-primary"
                name="state"
                value={data ? data.state : alertData.state}
                onChange={handleChange}
                placeholder="State"
              />
              <input
                className="form-control bg-primary"
                name="city"
                value={data ? data.city : alertData.city}
                onChange={handleChange}
                placeholder="City"
              />
            </div>

            {timeInputMode === 'setTime' && (
              <div className="d-flex flex-column gap-2">
                <div className="bg-primary p-2 rounded">
                  <label>
                    Start Time
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    className="form-control bg-primary"
                    type="datetime-local"
                    name="issuedTime"
                    value={moment(data ? data.issuedTime : alertData.issuedTime).format(
                      'YYYY-MM-DDTHH:mm',
                    )}
                    onChange={handleTimeString}
                    placeholder="Start Time"
                  />
                </div>

                <div className="bg-primary p-2 rounded">
                  <label>
                    Expiration Time
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    className="form-control bg-primary"
                    type="datetime-local"
                    name="expirationTime"
                    value={moment(data ? data.expirationTime : alertData.expirationTime).format(
                      'YYYY-MM-DDTHH:mm',
                    )}
                    onChange={handleTimeString}
                    placeholder="Expiration Time"
                  />
                </div>
              </div>
            )}

            <div className="d-flex flex-column flex-sm-row gap-2">
              <button className="btn btn-warning flex-fill" onClick={removeLastCoordinate}>
                Remove Last Coordinate
              </button>

              <button className="btn btn-danger flex-fill" onClick={removeAllCoordinates}>
                Remove ALL Coordinates
              </button>

              {!onSubmitCallback && (
                <button className="btn btn-success flex-fill" onClick={handleSubmit}>
                  Create Alert
                </button>
              )}
            </div>
          </div>
        </div>
      </Container>
    );

  if (widget) {
    if (dimensions[0] === 1 && dimensions[1] === 2)
      // current
      return (
        <Container fluid>
          <div className="d-flex flex-column gap-3 align-items-stretch">
            <div
              id="map-container"
              className="bg-primary flex-grow-full w-100 ratio-1"
              style={{ maxHeight: '500px' }}
            >
              <MoveablePoints
                mapCenter={mapCenter}
                zoom={zoom}
                alertData={!data ? alertData : data.coordinates ? data : { coordinates: [] }}
                setAlertData={setData ? updateData : setAlertData}
                isMobile={isMobile}
                bounds={bounds}
                setGetMapCenter={setGetMapCenter}
                jurisdictions={jurisdictions}
              />
            </div>

            <div className=" flex-grow-full d-flex flex-column gap-2">
              <div className="bg-primary p-2 rounded">
                <LocationSearch
                  alertData={data ? data : alertData}
                  setMapCenter={setMapCenter}
                  mapCenter={mapCenter}
                  handleChange={handleChange}
                  searchLocation={data ? data.searchLocation : alertData.searchLocation}
                  getMapCenter={getMapCenter}
                  setAlertData={setData ? updateData : setAlertData}
                />
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 w-100">
                <div className="d-flex flex-grow-full align-items-center gap-1 bg-primary p-2 rounded">
                  <div className="d-none d-sm-block">
                    <AlertIcons type={data ? data.type : alertData.type} />
                  </div>
                  <select
                    className="form-control bg-primary"
                    name="type"
                    value={data ? data.type : alertData.type}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Alert Type
                    </option>
                    {eventTypes.map((event) => (
                      <option key={event} value={event}>
                        {event}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-grow-full bg-primary p-2 rounded">
                  <div>
                    <p
                      className="rounded p-1"
                      style={{
                        backgroundColor: alertColorScale(data ? data.severity : alertData.severity),
                      }}
                    >
                      Severity: {getSeverityLevel(data ? data.severity : alertData.severity)}
                    </p>
                  </div>
                  <Slider
                    name="severity"
                    min={0}
                    max={100}
                    step={1}
                    value={100 - (data ? data.severity : alertData.severity)}
                    onChange={(event) => {
                      setData
                        ? updateData(
                            data
                              ? { ...data, severity: 100 - event }
                              : { ...alertData, severity: 100 - event },
                          )
                        : setAlertData(
                            data
                              ? { ...data, severity: 100 - event }
                              : { ...alertData, severity: 100 - event },
                          );
                    }}
                  />
                </div>
              </div>
              <input
                className="form-control bg-primary"
                name="headline"
                value={data ? data.headline : alertData.headline}
                onChange={handleChange}
                placeholder="Headline"
              />

              <textarea
                className="form-control bg-primary"
                name="description"
                value={data ? data.description : alertData.description}
                onChange={handleChange}
                placeholder="Description"
              />

              <div className="d-flex flex-row gap-1">
                <input
                  className="form-control bg-primary"
                  name="state"
                  value={data ? data.state : alertData.state}
                  onChange={handleChange}
                  placeholder="State"
                />
                <input
                  className="form-control bg-primary"
                  name="city"
                  value={data ? data.city : alertData.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>

              {timeInputMode === 'setTime' && (
                <div className="d-flex flex-column gap-2">
                  <div className="bg-primary p-2 rounded">
                    <label>Start Time</label>
                    <input
                      className="form-control bg-primary"
                      type="datetime-local"
                      name="issuedTime"
                      value={moment(data ? data.issuedTime : alertData.issuedTime).format(
                        'YYYY-MM-DDTHH:mm',
                      )}
                      onChange={handleTimeString}
                      placeholder="Start Time"
                    />
                  </div>

                  <div className="bg-primary p-2 rounded">
                    <label>Expiration Time</label>
                    <input
                      className="form-control bg-primary"
                      type="datetime-local"
                      name="expirationTime"
                      value={moment(data ? data.expirationTime : alertData.expirationTime).format(
                        'YYYY-MM-DDTHH:mm',
                      )}
                      onChange={handleTimeString}
                      placeholder="Expiration Time"
                    />
                  </div>
                </div>
              )}

              <div className="d-flex flex-column flex-sm-row gap-2">
                <button className="btn btn-warning" onClick={removeLastCoordinate}>
                  Remove Last Coordinate
                </button>

                <button className="btn btn-danger" onClick={removeAllCoordinates}>
                  Remove ALL Coordinates
                </button>

                {!onSubmitCallback && (
                  <button className="btn btn-success" onClick={handleSubmit}>
                    Create Alert
                  </button>
                )}
              </div>
            </div>
          </div>
        </Container>
      );

    if (dimensions[0] === 2 && dimensions[1] === 1)
      return (
        <Container fluid>
          <div className="d-flex flex-column flex-lg-row gap-3 align-items-stretch">
            <div id="map-container" className="bg-primary flex-grow-full h-100 ratio-1">
              <MoveablePoints
                mapCenter={mapCenter}
                zoom={zoom}
                alertData={!data ? alertData : data.coordinates ? data : { coordinates: [] }}
                setAlertData={setData ? updateData : setAlertData}
                isMobile={isMobile}
                bounds={bounds}
                setGetMapCenter={setGetMapCenter}
                jurisdictions={jurisdictions}
              />
            </div>

            <div className=" flex-grow-full d-flex flex-column gap-2">
              <div className="bg-primary p-2 rounded">
                <LocationSearch
                  alertData={data ? data : alertData}
                  setMapCenter={setMapCenter}
                  mapCenter={mapCenter}
                  handleChange={handleChange}
                  searchLocation={data ? data.searchLocation : alertData.searchLocation}
                  getMapCenter={getMapCenter}
                  setAlertData={setData ? updateData : setAlertData}
                />
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 w-100">
                <div className="d-flex flex-grow-full align-items-center gap-1 bg-primary p-2 rounded">
                  <div className="d-none d-sm-block">
                    <AlertIcons type={data ? data.type : alertData.type} />
                  </div>
                  <select
                    className="form-control bg-primary"
                    name="type"
                    value={data ? data.type : alertData.type}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Alert Type
                    </option>
                    {eventTypes.map((event) => (
                      <option key={event} value={event}>
                        {event}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-grow-full bg-primary p-2 rounded">
                  <div>
                    <p
                      className="rounded p-1"
                      style={{
                        backgroundColor: alertColorScale(data ? data.severity : alertData.severity),
                      }}
                    >
                      Severity: {getSeverityLevel(data ? data.severity : alertData.severity)}
                    </p>
                  </div>
                  <Slider
                    name="severity"
                    min={0}
                    max={100}
                    step={1}
                    value={100 - (data ? data.severity : alertData.severity)}
                    onChange={(event) => {
                      setData
                        ? updateData(
                            data
                              ? { ...data, severity: 100 - event }
                              : { ...alertData, severity: 100 - event },
                          )
                        : setAlertData(
                            data
                              ? { ...data, severity: 100 - event }
                              : { ...alertData, severity: 100 - event },
                          );
                    }}
                  />
                </div>
              </div>
              <input
                className="form-control bg-primary"
                name="headline"
                value={data ? data.headline : alertData.headline}
                onChange={handleChange}
                placeholder="Headline"
              />

              <textarea
                className="form-control bg-primary"
                name="description"
                value={data ? data.description : alertData.description}
                onChange={handleChange}
                placeholder="Description"
              />

              <div className="d-flex flex-row gap-1">
                <input
                  className="form-control bg-primary"
                  name="state"
                  value={data ? data.state : alertData.state}
                  onChange={handleChange}
                  placeholder="State"
                />
                <input
                  className="form-control bg-primary"
                  name="city"
                  value={data ? data.city : alertData.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>

              {timeInputMode === 'setTime' && (
                <div className="d-flex flex-column gap-2">
                  <div className="bg-primary p-2 rounded">
                    <label>Start Time</label>
                    <input
                      className="form-control bg-primary"
                      type="datetime-local"
                      name="issuedTime"
                      value={moment(data ? data.issuedTime : alertData.issuedTime).format(
                        'YYYY-MM-DDTHH:mm',
                      )}
                      onChange={handleTimeString}
                      placeholder="Start Time"
                    />
                  </div>

                  <div className="bg-primary p-2 rounded">
                    <label>Expiration Time</label>
                    <input
                      className="form-control bg-primary"
                      type="datetime-local"
                      name="expirationTime"
                      value={moment(data ? data.expirationTime : alertData.expirationTime).format(
                        'YYYY-MM-DDTHH:mm',
                      )}
                      onChange={handleTimeString}
                      placeholder="Expiration Time"
                    />
                  </div>
                </div>
              )}

              {/* {timeInputMode === "duration" && (
                <div>
                  <div>
                    Duration:{" "}
                    {(data ? data.duration : alertData.duration) === 0
                      ? "Indefinite"
                      : (data ? data.duration : alertData.duration) +
                        " hour" +
                        ((data ? data.duration : alertData.duration) === 1
                          ? ""
                          : "s")}{" "}
                  </div>
                  <Slider
                    name='duration'
                    min={0}
                    max={24}
                    step={1}
                    value={data ? data.duration : alertData.duration}
                    onChange={(event) => {
                      setData
                        ? updateData(
                            data
                              ? { ...data, duration: event }
                              : { ...alertData, duration: event }
                          )
                        : setAlertData(
                            data
                              ? { ...data, duration: event }
                              : { ...alertData, duration: event }
                          );
                    }}
                  />
                </div>
              )} */}

              <div className="d-flex flex-column flex-sm-row gap-2">
                <button className="btn btn-warning flex-fill" onClick={removeLastCoordinate}>
                  Remove Last Coordinate
                </button>

                <button className="btn btn-danger flex-fill" onClick={removeAllCoordinates}>
                  Remove ALL Coordinates
                </button>

                {!onSubmitCallback && (
                  <button className="btn btn-success flex-fill" onClick={handleSubmit}>
                    Create Alert
                  </button>
                )}
              </div>
            </div>
          </div>
        </Container>
      );
  }
}

const severities = ['Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown'];

const states = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const eventTypes = [
  '911 Telephone Outage Emergency',
  'Administrative Message',
  'Air Quality Alert',
  'Air Stagnation Advisory',
  'Arroyo And Small Stream Flood Advisory',
  'Ashfall Advisory',
  'Ashfall Warning',
  'Avalanche Advisory',
  'Avalanche Warning',
  'Avalanche Watch',
  'Beach Hazards Statement',
  'Blizzard Warning',
  'Blizzard Watch',
  'Blowing Dust Advisory',
  'Blowing Dust Warning',
  'Brisk Wind Advisory',
  'Child Abduction Emergency',
  'Civil Danger Warning',
  'Civil Emergency Message',
  'Coastal Flood Advisory',
  'Coastal Flood Statement',
  'Coastal Flood Warning',
  'Coastal Flood Watch',
  'Dense Fog Advisory',
  'Dense Smoke Advisory',
  'Dust Advisory',
  'Dust Storm Warning',
  'Earthquake Warning',
  'Evacuation - Immediate',
  'Excessive Heat Warning',
  'Excessive Heat Watch',
  'Extreme Cold Warning',
  'Extreme Cold Watch',
  'Extreme Fire Danger',
  'Extreme Wind Warning',
  'Fire Warning',
  'Fire Weather Watch',
  'Flash Flood Statement',
  'Flash Flood Warning',
  'Flash Flood Watch',
  'Flood Advisory',
  'Flood Statement',
  'Flood Warning',
  'Flood Watch',
  'Freeze Warning',
  'Freeze Watch',
  'Freezing Fog Advisory',
  'Freezing Rain Advisory',
  'Freezing Spray Advisory',
  'Frost Advisory',
  'Gale Warning',
  'Gale Watch',
  'Hard Freeze Warning',
  'Hard Freeze Watch',
  'Hazardous Materials Warning',
  'Hazardous Seas Warning',
  'Hazardous Seas Watch',
  'Hazardous Weather Outlook',
  'Heat Advisory',
  'Heavy Freezing Spray Warning',
  'Heavy Freezing Spray Watch',
  'High Surf Advisory',
  'High Surf Warning',
  'High Wind Warning',
  'High Wind Watch',
  'Hurricane Force Wind Warning',
  'Hurricane Force Wind Watch',
  'Hurricane Local Statement',
  'Hurricane Warning',
  'Hurricane Watch',
  'Hydrologic Advisory',
  'Hydrologic Outlook',
  'Ice Storm Warning',
  'Lake Effect Snow Advisory',
  'Lake Effect Snow Warning',
  'Lake Effect Snow Watch',
  'Lake Wind Advisory',
  'Lakeshore Flood Advisory',
  'Lakeshore Flood Statement',
  'Lakeshore Flood Warning',
  'Lakeshore Flood Watch',
  'Law Enforcement Warning',
  'Local Area Emergency',
  'Low Water Advisory',
  'Marine Weather Statement',
  'Nuclear Power Plant Warning',
  'Radiological Hazard Warning',
  'Red Flag Warning',
  'Rip Current Statement',
  'Severe Thunderstorm Warning',
  'Severe Thunderstorm Watch',
  'Severe Weather Statement',
  'Shelter In Place Warning',
  'Short Term Forecast',
  'Small Craft Advisory',
  'Small Craft Advisory For Hazardous Seas',
  'Small Craft Advisory For Rough Bar',
  'Small Craft Advisory For Winds',
  'Small Stream Flood Advisory',
  'Snow Squall Warning',
  'Special Marine Warning',
  'Special Weather Statement',
  'Storm Surge Warning',
  'Storm Surge Watch',
  'Storm Warning',
  'Storm Watch',
  'Test',
  'Tornado Warning',
  'Tornado Watch',
  'Tropical Depression Local Statement',
  'Tropical Storm Local Statement',
  'Tropical Storm Warning',
  'Tropical Storm Watch',
  'Tsunami Advisory',
  'Tsunami Warning',
  'Tsunami Watch',
  'Typhoon Local Statement',
  'Typhoon Warning',
  'Typhoon Watch',
  'Urban And Small Stream Flood Advisory',
  'Volcano Warning',
  'Wind Advisory',
  'Wind Chill Advisory',
  'Wind Chill Warning',
  'Wind Chill Watch',
  'Winter Storm Warning',
  'Winter Storm Watch',
  'Winter Weather Advisory',
];
